import { CircularProgress } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import FeeModelsDisplay from 'src/mentor/components/ActiveFeeModels/components/FeeModelsDisplay'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { getMinimalMentorData } from 'src/supportPortal/views/therapists/util'

const SERVICE_TABS_BY_ROLE = {
  ROLE_THERAPIST: {
    id: 'ROLE_THERAPIST',
    title: 'Therapy & Assessment',
    eventTypes: ['therapy-session', 'assessment'],
  },
  ROLE_SCREENER: {
    id: 'ROLE_SCREENER',
    title: 'Screening',
    eventTypes: ['screening'],
  },
  ROLE_DOCTOR: {
    id: 'ROLE_DOCTOR',
    title: 'Consultation',
    eventTypes: ['consultation', 'nutrition-consultation'],
  },
}

function getServiceTabsForPractitioner(practitioner) {
  const servicesApproved = practitioner?.teacherUnEditableFields?.servicesApproved || {}
  return Object.entries(SERVICE_TABS_BY_ROLE).reduce((tabs, [role, tab]) => {
    if (servicesApproved?.[role]?.enabled) {
      tabs.push(tab)
    }
    return tabs
  }, [])
}

export default function TherapistEngagementModels() {
  const { mentorSlug } = useParams()
  const [loading, setLoading] = useState(false)
  const [currentTherapist, setCurrentTherapist] = useState()
  const [loadingModels, setLoadingModels] = useState(false)
  const [entityFeeModels, setEntityFeeModels] = useState()
  const [inApplicableEntityFeeModels, setInApplicableEntityFeeModels] = useState()
  const [feeModels, setFeeModels] = useState()
  const [feeModelGroups, setFeeModelGroups] = useState()
  const getSelectedTherapist = useCallback(async () => {
    const resp = await getMinimalMentorData(
      mentorSlug,
      true,
      `/api/secure/cc/support/therapists/by-slug?mentor-slug=${mentorSlug}`,
    )
    setCurrentTherapist(resp)
    return resp
  }, [mentorSlug])

  const getFeeModels = useCallback(async (therapistData) => {
    if (!therapistData?.id) return

    setLoadingModels(true)
    const resp = await getSPDataWithParams('/api/secure/cc/support/entity-fee-model/mentor/list', {
      mentorId: therapistData.id,
    })
    if (resp.status === 200) {
      if (isCommonResponseSuccessfulV2(resp)) {
        const data = getDataMapFromCommonResponse(resp)
        const entityFeeModelsResp = data?.['entity-fee-model']
        const inApplicableEntityFeeModelsResp = data?.['inApplicable__entity-fee-model']
        setEntityFeeModels(entityFeeModelsResp)
        setInApplicableEntityFeeModels(inApplicableEntityFeeModelsResp)
        setFeeModels(data?.['fee-model'])
      }
    }
    setLoadingModels(false)
  }, [])

  const getFeeModelGroups = useCallback(async () => {
    const resp = await getSPDataWithParams(
      '/api/secure/cc/support/entity-fee-model/fee-model-group/list',
    )
    if (resp.status === 200) {
      if (isCommonResponseSuccessfulV2(resp)) {
        const data = getDataMapFromCommonResponse(resp)
        setFeeModelGroups(data?.['fee-model-group'])
      }
    }
  }, [])

  const setup = useCallback(async () => {
    setLoading(true)
    const therapistData = await getSelectedTherapist()
    if (therapistData?.id) {
      await getFeeModels(therapistData)
      await getFeeModelGroups()
    }
    setLoading(false)
  }, [getFeeModelGroups, getFeeModels, getSelectedTherapist])

  useEffect(() => {
    setup()
  }, [setup])

  return (
    <div className="d-flex flex-column h-100">
      <SupportHeader
        heading={`${currentTherapist?.firstName || 'Therapist'} Engagement Models`}
      />
      {loading && !currentTherapist ? (
        <div className="d-flex justify-content-center align-items-center h-100 w-100">
          <CircularProgress />
        </div>
      ) : currentTherapist ? (
        <FeeModelsDisplay
          entityFeeModels={entityFeeModels}
          inApplicableEntityFeeModels={inApplicableEntityFeeModels}
          feeModels={feeModels}
          feeModelGroups={feeModelGroups}
          forMentor={true}
          loading={loadingModels}
          serviceTabs={getServiceTabsForPractitioner(currentTherapist)}
        />
      ) : (
        <div className="d-flex justify-content-center align-items-center h-100 w-100">
          <CircularProgress />
        </div>
      )}
    </div>
  )
}
